import type { WatchOut } from "./api";

/** What each level means in operator language. L1/L2/L3 is the storage grain;
 *  nobody outside this feature thinks in those terms. */
const LEVELS = {
  L1: "Integration",
  L2: "Company",
  L3: "Location",
} as const;

export const LEVEL_LABEL: Record<string, string> = LEVELS;

/** Anything the monitor keys on an entity: incidents, first-seen rows, watch
 *  entries, profiles. Every one of them carries the same denormalized identity
 *  columns, so one rule can read all of them. */
export interface EntityLike {
  level: string;
  key_path: string;
  entity_source?: string | null;
  company_id?: string | null;
  company_name?: string | null;
  location_id?: string | null;
  location_name?: string | null;
}

/** One link in the chain from integration down to the row's own grain. */
export interface ChainLink {
  level: string;
  /** Operator word for the level — "Integration" / "Company" / "Location". */
  levelLabel: string;
  /** Readable name, or null when we could only resolve an id. */
  name: string | null;
  /** The raw id/source behind it — shown when there is no name. */
  id: string;
  /** key_path that addresses this link, for drilling to it. */
  keyPath: string;
  /** True for the link the row is actually ABOUT. */
  isSubject: boolean;
}

/**
 * The full L1 → L2 → L3 chain for a row, top down.
 *
 * This is the answer to "where do I look?": an L3 incident is not "a
 * jiffylube_sftp problem", it is *this location*, inside *this company*,
 * arriving over *this integration* — and an operator needs all three to act.
 * Every surface (table cell, quick drawer, expanded drawer) renders the same
 * chain so the vocabulary never shifts between views.
 *
 * Links are emitted only down to the row's own level, and each carries the
 * key_path that addresses it so any link can be made a drill target.
 */
export function entityChain(r: EntityLike): [ChainLink, ...ChainLink[]] {
  const source = r.entity_source ?? r.key_path.split("/")[0] ?? "";
  const depth = r.level === "L3" ? 3 : r.level === "L2" ? 2 : 1;
  const head: ChainLink = {
    level: "L1",
    levelLabel: LEVELS.L1,
    // A source key IS its own name — there is no prettier form of "grow".
    name: source || null,
    id: source,
    keyPath: source,
    isSubject: depth === 1,
  };
  const rest: ChainLink[] = [];
  if (depth >= 2 && r.company_id) {
    rest.push({
      level: "L2",
      levelLabel: LEVELS.L2,
      name: r.company_name ?? null,
      id: r.company_id,
      keyPath: `${source}/${r.company_id}`,
      isSubject: depth === 2,
    });
  }
  if (depth >= 3 && r.location_id) {
    rest.push({
      level: "L3",
      levelLabel: LEVELS.L3,
      name: r.location_name ?? null,
      id: r.location_id,
      keyPath: `${source}/${r.company_id ?? ""}/${r.location_id}`,
      isSubject: true,
    });
  }
  return [head, ...rest];
}

/** The thing that is actually broken — the deepest link, which is what the row
 *  is about. An L2 row is about the COMPANY; leading with the integration is
 *  what makes a company-level break read as "the whole feed is down". */
export function entitySubject(r: EntityLike): ChainLink {
  const [head, ...rest] = entityChain(r);
  return rest.at(-1) ?? head;
}

/** The trail ABOVE the subject, as text: the integration for a company,
 *  integration + company for a location.
 *
 *  Ids appear only where they carry information — an unresolved company still
 *  shows its id, because *which* company matters more than whether we could
 *  name it. The subject's own id is left out: it says nothing a human reads,
 *  and EntityName already falls back to the key_path when the name is missing.
 */
export function entityContext(r: EntityLike): string {
  const parents = entityChain(r).filter((l) => !l.isSubject);
  if (!parents.length) return "Whole integration";
  return parents.map((l) => l.name ?? l.id).join(" · ");
}

/** What the operator is actually watching, at this row's level. Null means we
 *  could not resolve a name — callers fall back to the raw key. */
export function watchName(w: WatchOut): string | null {
  return entitySubject(w).name;
}

/** Trail above a watch row. See {@link entityContext}. */
export function watchContext(w: WatchOut): string {
  return entityContext(w);
}
